import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BsdfGlossySockets} from "#material/nodes/shader/bsdf-glossy/definition"
import {bsdfGlossyDefinition} from "#material/nodes/shader/bsdf-glossy/definition"
import type {ThreeEvaluator, CyclesEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {GlossyBsdfNode} from "#material/cycles/types"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import {cyclesDistribution} from "#material/nodes/shader/bsdf-glossy/helpers"
import {threeConvert, threeNormalFromVec3, threeRGBColor, threeValue} from "#material/three/three-utils"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"

@registerMaterialGraphNode
export class BsdfGlossy extends MaterialNode<BsdfGlossySockets> {
    public get definition() {
        return bsdfGlossyDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BsdfGlossySockets>> => {
        const material = new ThreeColormassMaterial()

        const {color, roughness, normal} = await evaluator.getAll(this.parameters.inputs)

        material.colorNode = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? material.colorNode
        material.roughnessNode = roughness?.toFloat() ?? threeConvert(this.parameters.settings.roughness, threeValue) ?? material.roughnessNode
        material.metalnessNode = threeValue(0)

        material.normalNode =
            normal?.toVec3() ?? (this.parameters.settings.normal ? threeNormalFromVec3(this.parameters.settings.normal) : undefined) ?? material.normalNode

        return {bsdf: material}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BsdfGlossySockets>> => {
        const bsdf: GlossyBsdfNode = {
            type: "glossy_bsdf",
            inputs: await evaluator.getAll({
                color: this.parameters.inputs.color,
                normal: this.parameters.inputs.normal,
                roughness: this.parameters.inputs.roughness,
                anisotropy: this.parameters.inputs.anisotropy,
                rotation: this.parameters.inputs.rotation,
                tangent: this.parameters.inputs.tangent,
                surface_mix_weight: undefined,
            }),
            parameters: {
                color: cyclesColor(this.parameters.settings.color),
                roughness: this.parameters.settings.roughness,
                normal: cyclesVec3(this.parameters.settings.normal),
                anisotropy: this.parameters.settings.anisotropy,
                rotation: this.parameters.settings.rotation,
                tangent: cyclesVec3(this.parameters.settings.tangent),
                surface_mix_weight: undefined,
                distribution: cyclesDistribution(this.parameters.settings.distribution),
            },
            resources: {},
        }

        return {bsdf: [bsdf, "bsdf"]}
    }
}
